/**
 * QueueStatusIndicator - Shows pending offline operations count and sync status
 */
import React, { useState } from 'react';
import { Cloud, CloudOff, RefreshCw, AlertCircle, X, AlertTriangle } from 'lucide-react';
import { useOfflineQueue } from '../hooks/useOfflineQueue';
import { showToast } from '../utils/toast';
import ConflictResolutionModal from './ConflictResolutionModal';
import BatchReplayStatus from './BatchReplayStatus';

const QueueStatusIndicator = () => {
  const {
    isOnline,
    connectionQualityGood,
    pendingCount,
    isReplaying,
    queue,
    syncNow,
    clearFailedOperations,
    conflictCount,
    resolveConflict,
    batchProgress,
  } = useOfflineQueue();

  const [showDetails, setShowDetails] = React.useState(false);
  const [selectedConflict, setSelectedConflict] = useState(null);

  const failedCount = queue?.filter(op => op?.status === 'failed')?.length;

  // Don't show if no pending operations and online
  if (pendingCount === 0 && conflictCount === 0 && isOnline && connectionQualityGood) {
    return (
      <>
        {/* Show batch progress even when queue is empty */}
        <BatchReplayStatus batchProgress={batchProgress} />
      </>
    );
  }

  const handleSyncNow = async () => {
    try {
      await syncNow();
    } catch (error) {
      showToast('Failed to sync operations', 'error');
    }
  };

  const handleClearFailed = () => {
    clearFailedOperations();
    showToast('Failed operations cleared', 'success');
  };

  const handleResolveConflict = async (operationId, resolution) => {
    await resolveConflict(operationId, resolution);
    setSelectedConflict(null);
  };

  return (
    <>
      {/* Offline Banner */}
      {(!isOnline || !connectionQualityGood) && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="bg-yellow-600 text-white px-4 py-2 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CloudOff className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">
                  {!isOnline ? 'Offline' : 'Poor connection'} - changes will sync when online
                </p>
              </div>
              {pendingCount > 0 && (
                <span className="text-xs bg-white text-yellow-600 px-2 py-1 rounded-full font-semibold">
                  {pendingCount} pending
                </span>
              )}
            </div>
          </div>
        </div>
      )}
      {/* Conflict Alert Banner */}
      {conflictCount > 0 && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div className="bg-orange-600 text-white px-4 py-2 shadow-lg">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <p className="text-sm font-medium">
                  {conflictCount} operation{conflictCount > 1 ? 's' : ''} require manual resolution
                </p>
              </div>
              <button
                onClick={() => setShowDetails(true)}
                className="text-xs bg-white text-orange-600 px-3 py-1 rounded-full font-semibold hover:bg-orange-50 transition-colors"
              >
                Review
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Queue Status Badge (Bottom Right) */}
      {(pendingCount > 0 || conflictCount > 0) && (
        <div className="fixed bottom-20 right-4 z-40">
          <button
            onClick={() => setShowDetails(!showDetails)}
            className="relative flex items-center gap-2 bg-card border border-border rounded-full px-4 py-2 shadow-lg hover:shadow-xl transition-all duration-250"
          >
            {isReplaying ? (
              <RefreshCw className="w-4 h-4 text-primary animate-spin" />
            ) : conflictCount > 0 ? (
              <AlertTriangle className="w-4 h-4 text-warning" />
            ) : isOnline && connectionQualityGood ? (
              <Cloud className="w-4 h-4 text-success" />
            ) : (
              <CloudOff className="w-4 h-4 text-warning" />
            )}
            
            <span className="text-sm font-medium text-foreground">
              {isReplaying ? 'Syncing...' : conflictCount > 0 ? `${conflictCount} conflicts` : `${pendingCount} pending`}
            </span>

            {(failedCount > 0 || conflictCount > 0) && (
              <span className="absolute -top-1 -right-1 w-5 h-5 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center text-xs font-bold">
                {failedCount + conflictCount}
              </span>
            )}
          </button>

          {/* Details Panel */}
          {showDetails && (
            <div className="absolute bottom-full right-0 mb-2 w-80 bg-popover border border-border rounded-lg shadow-xl p-4 animate-scale-in">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold text-foreground">Offline Queue</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="text-muted-foreground hover:text-foreground"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2 mb-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Status:</span>
                  <span className="font-medium text-foreground">
                    {isOnline && connectionQualityGood ? 'Online' : isOnline ? 'Poor connection' : 'Offline'}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Pending:</span>
                  <span className="font-medium text-foreground">{pendingCount}</span>
                </div>
                {conflictCount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Conflicts:</span>
                    <span className="font-medium text-warning">{conflictCount}</span>
                  </div>
                )}
                {failedCount > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Failed:</span>
                    <span className="font-medium text-destructive">{failedCount}</span>
                  </div>
                )}
              </div>

              {/* Statistics with batch info */}
              {batchProgress?.stats && (
                <div className="mb-3 p-2 bg-primary/10 rounded-lg">
                  <div className="text-xs font-semibold text-primary mb-1">Batch Optimization</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div>
                      <span className="text-muted-foreground">Original:</span>
                      <span className="ml-1 font-medium text-foreground">{batchProgress?.stats?.originalCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Optimized:</span>
                      <span className="ml-1 font-medium text-success">{batchProgress?.stats?.optimizedCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Batches:</span>
                      <span className="ml-1 font-medium text-foreground">{batchProgress?.stats?.batchCount}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Saved:</span>
                      <span className="ml-1 font-medium text-success">-{batchProgress?.stats?.savedOperations}</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Operation List */}
              <div className="max-h-48 overflow-y-auto mb-3 space-y-2">
                {queue?.slice(0, 10)?.map((op) => (
                  <div
                    key={op?.id}
                    className="flex items-center gap-2 p-2 bg-muted rounded text-xs"
                  >
                    {op?.status === 'replaying' ? (
                      <RefreshCw className="w-3 h-3 text-primary animate-spin flex-shrink-0" />
                    ) : op?.status === 'conflict' ? (
                      <AlertTriangle className="w-3 h-3 text-warning flex-shrink-0" />
                    ) : op?.status === 'failed' ? (
                      <AlertCircle className="w-3 h-3 text-destructive flex-shrink-0" />
                    ) : (
                      <Cloud className="w-3 h-3 text-muted-foreground flex-shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-foreground font-medium truncate">
                        {op?.operationType}
                      </p>
                      <p className="text-muted-foreground text-xs">
                        {new Date(op.timestamp)?.toLocaleTimeString()}
                      </p>
                    </div>
                    {op?.status === 'conflict' && (
                      <button
                        onClick={() => setSelectedConflict(op)}
                        className="text-xs text-warning hover:text-warning/80 font-medium"
                      >
                        Resolve
                      </button>
                    )}
                    {op?.retryCount > 0 && op?.status !== 'conflict' && (
                      <span className="text-xs text-muted-foreground">
                        {op?.retryCount} retries
                      </span>
                    )}
                  </div>
                ))}
                {queue?.length > 10 && (
                  <p className="text-xs text-muted-foreground text-center">
                    +{queue?.length - 10} more
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="space-y-2">
                <button
                  onClick={handleSyncNow}
                  disabled={isReplaying || !isOnline}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 bg-primary text-primary-foreground rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <RefreshCw className={`w-4 h-4 ${isReplaying ? 'animate-spin' : ''}`} />
                  {isReplaying ? 'Syncing...' : 'Sync Now'}
                </button>

                {failedCount > 0 && (
                  <button
                    onClick={handleClearFailed}
                    className="w-full px-3 py-2 bg-destructive/10 text-destructive rounded-lg text-sm font-medium hover:bg-destructive/20 transition-colors"
                  >
                    Clear Failed Operations
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}
      {/* Conflict Resolution Modal */}
      {selectedConflict && (
        <ConflictResolutionModal
          operation={selectedConflict}
          onResolve={handleResolveConflict}
          onClose={() => setSelectedConflict(null)}
        />
      )}
      {/* Batch Replay Status */}
      <BatchReplayStatus batchProgress={batchProgress} />
    </>
  );
};

export default QueueStatusIndicator;
